import React, { useEffect } from 'react';
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebaseConfig";
import './RinkCard.css';

// Helper function to get condition color and label
const getConditionInfo = (condition) => {
  const conditionMap = {
    'Excellente': { color: 'excellent', label: 'Excellente', emoji: '⭐' },
    'Bonne': { color: 'good', label: 'Bonne', emoji: '👍' },
    'Mauvaise': { color: 'bad', label: 'Mauvaise', emoji: '⚠️' },
    'N/A': { color: 'unknown', label: 'Non disponible', emoji: '❓' }
  };

  // Return matched condition, or fallback to displaying the raw string with 'unknown' style
  if (conditionMap[condition]) {
    return conditionMap[condition];
  }

  if (!condition || condition === 'Inconnu') {
    return conditionMap['N/A'];
  }

  // Dynamic condition (e.g. "Entretien en cours", "Hors-saison")
  return { color: 'unknown', label: condition, emoji: 'ℹ️' };
};

// Helper to format date nicely
const formatDate = (dateString) => {
  if (!dateString) return 'Non disponible';
  const date = new Date(dateString.replace(' ', 'T'));
  const now = new Date();

  // Get date parts for comparison (ignoring time)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Format time part
  const timeStr = date.toLocaleTimeString('fr-CA', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Check if today or yesterday
  if (dateOnly.getTime() === today.getTime()) {
    return `Aujourd'hui à ${timeStr}`;
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    return `Hier à ${timeStr}`;
  }

  // Otherwise show full date
  return date.toLocaleDateString('fr-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const RinkCard = ({ rink, onClose }) => {
  useEffect(() => {
    if (rink && analytics) {
      logEvent(analytics, 'view_item', {
        item_id: rink.id || rink.nom, // fallback to name if no ID
        item_name: rink.nom,
        item_category: 'rink',
        location_id: rink.arrondissement?.nom_arr
      });
    }
  }, [rink]);

  if (!rink) return null;

  const isOpen = rink.ouvert == 1;
  const conditionInfo = getConditionInfo(rink.condition);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rink.address)}`;

  const handleGetDirections = () => {
    if (analytics) {
      logEvent(analytics, 'get_directions', {
        item_id: rink.id || rink.nom,
        destination: rink.address
      });
    }
  };

  return (
    <div className="rink-card animate-slideUp">
      {/* Close button */}
      <button className="rink-card__close" onClick={onClose} aria-label="Fermer">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Status badge */}
      <div className={`rink-card__status ${isOpen ? 'rink-card__status--open' : 'rink-card__status--closed'}`}>
        <span className="rink-card__status-dot"></span>
        {isOpen ? 'Ouverte' : 'Fermée'}
      </div>

      {/* Title */}
      <h2 className="rink-card__title">{rink.nom}</h2>

      {/* Arrondissement */}
      <p className="rink-card__arrondissement">{rink.arrondissement?.nom_arr}</p>

      {/* Info rows */}
      <div className="rink-card__info">
        {/* Address */}
        <div className="rink-card__row">
          <span className="rink-card__icon">📍</span>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rink-card__link"
            onClick={handleGetDirections}
          >
            {rink.address}
          </a>
        </div>

        {/* Condition */}
        <div className="rink-card__row">
          <span className="rink-card__icon">{conditionInfo.emoji}</span>
          <span className="rink-card__label">Condition:</span>
          <span className={`rink-card__condition rink-card__condition--${conditionInfo.color}`}>
            {conditionInfo.label}
          </span>
        </div>

        {/* Last update */}
        <div className="rink-card__row">
          <span className="rink-card__icon">🕐</span>
          <span className="rink-card__label">Mise à jour:</span>
          <span className="rink-card__value">{formatDate(rink.arrondissement?.date_maj)}</span>
        </div>

        {/* Extra info badges */}
        <div className="rink-card__badges">
          {rink.deblaye === '1' && (
            <span className="rink-card__badge rink-card__badge--info" title="Déblayée">
              ❄️ Déblayée
            </span>
          )}
          {rink.arrose === '1' && (
            <span className="rink-card__badge rink-card__badge--info" title="Arrosée">
              💧 Arrosée
            </span>
          )}
          {rink.resurface === '1' && (
            <span className="rink-card__badge rink-card__badge--info" title="Resurfacée">
              ✨ Resurfacée
            </span>
          )}
        </div>
      </div>

      {/* Action button */}
      <a
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="rink-card__button"
        onClick={handleGetDirections}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
        Obtenir l'itinéraire
      </a>
    </div>
  );
};

export default RinkCard;
